'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { type CSSProperties } from 'react'
import { PRODUCT_PLACEHOLDER_IMAGE } from '@/constants/catalog'
import { useAddToCart } from '@/hooks/business/cart'
import type { IProductCard } from '@/schemas/catalog/product'
import { formatBRL } from '~/helpers/formatCurrency'
import { safeImageSrc } from '~/helpers/safeImageSrc'
import DiscountBadge from '../DiscountBadge'
import { showProductAlert } from './showProductAlert'

interface IProductCardProps {
    data: IProductCard
    maxSize?: boolean
}

/** Imagem que aparece no hover: a de ordem 2 (a mão com a luva), senão a primeira. */
const hoverImageOf = (product: IProductCard) =>
    product.pictures.find((picture) => picture.order === 2)?.url ||
    product.pictures[0]?.url ||
    product.imagesURL[0] ||
    PRODUCT_PLACEHOLDER_IMAGE

/**
 * Card de produto (listagem, vitrines, recomendações e carrinho). O ícone de carrinho adiciona uma unidade e mostra o
 * alerta com o produto; o erro (produto repetido, sem estoque) sai no toast do domínio do carrinho.
 */
const ProductCard: React.FC<IProductCardProps> = ({ data, maxSize = false }) => {
    const { addToCart, isAdding } = useAddToCart()
    const hasStock = data.stockQuantity !== 0
    const [priceInt, priceCents] = formatBRL(data.discountedPrice).split(',')
    const [installmentInt, installmentCents] = formatBRL(data.discountedPrice / 3).split(',')
    // A cor do hover vem do cadastro (hex): vai numa variável CSS lida por classe fixa, que o Tailwind gera.
    const hoverColor = { '--product-color': data.mainPageColor || 'var(--color-tertiary)' } as CSSProperties

    const handleAddToCart = async () => {
        try {
            await addToCart(data.id, 1)
            showProductAlert('Produto adicionado ao carrinho!', 'success', data)
        } catch {
            // O toast do domínio do carrinho já mostrou a mensagem da API.
        }
    }

    return (
        <div
            className={`relative flex flex-col grow ${maxSize ? 'min-w-[160px]' : ' w-11/12'}  mx-auto h-full  bg-gray rounded-3xl `}
        >
            <DiscountBadge price={data.price} discountedPrice={data.discountedPrice} />

            <Link href={`/produtos/${data.slug}`}>
                <div
                    className="relative w-full aspect-square group hover:rounded-full hover:bg-(--product-color) transition-colors duration-500 rounded-full"
                    style={hoverColor}
                >
                    <Image
                        title={data.name}
                        src={safeImageSrc(data.thumbnailUrl)}
                        alt={data.name}
                        className="absolute z-10 object-cover object-center w-full h-full transition-all duration-500 transform scale-100 rounded-t-3xl rounded-b-md group-hover:scale-90 group-hover:opacity-0"
                        width={1000}
                        height={1000}
                        quality={100}
                    />
                    <Image
                        title={data.name}
                        src={safeImageSrc(hoverImageOf(data))}
                        alt={data.name}
                        className="absolute object-cover object-center w-full h-full transition-all duration-500 transform scale-0 rounded-full opacity-0 group-hover:scale-100 group-hover:opacity-100"
                        width={1000}
                        height={1000}
                        quality={100}
                    />
                </div>
            </Link>

            <div className="flex flex-col justify-between grow p-3 max-sm:pb-10">
                <div className="flex flex-col justify start">
                    <Link href={`/produtos/${data.slug}`}>
                        <p className="font-bold text-md max-md:text-lg text-blue-950 h-fit text-pretty ">{data.name}</p>
                    </Link>

                    <span className="pt-1 text-xs font-normal text-gray-400 text-pretty ">{data.shortDescription}</span>
                </div>

                <div className="flex flex-row items-center mt-8">
                    <div className="flex flex-col grow">
                        {data.price > data.discountedPrice && (
                            <p className="line-through text-s max-sm:text-sm text-light-blue-inactive">
                                {formatBRL(data.price)}
                            </p>
                        )}
                        <p className={`text-2xl max-sm:text-xl font-black text-accent`}>
                            {priceInt}
                            <span className=" align-top font-black text-sm pt-2 ps-1">,{priceCents}</span>
                        </p>
                    </div>
                    <p className="text-xs max-sm:text-sm text-primary max-w-28 text-end">
                        ou 3x de {installmentInt},{installmentCents}
                        <br />
                        sem juros*
                    </p>
                </div>

                <div className="flex flex-col items-center justify-end flex-shrink-0 w-full mt-4 grow">
                    <div
                        // Uma direção só: com flex-row e flex-col-reverse juntas, a ordem do CSS do Tailwind 4 fazia a flex-row vencer.
                        className={`relative flex items-center flex-shrink-0 gap-2 w-full ${hasStock ? 'flex-row' : 'flex-col-reverse'}`}
                    >
                        <Link
                            href={`/produtos/${data.slug}`}
                            className="basis-4/5 w-full relative bg-tertiary-green hover:bg-primary-green text-center z-20 rounded-full text-[14px] text-white px-6 pt-2 pb-2 transition-all duration-200"
                        >
                            quero comprar
                        </Link>
                        {hasStock ? (
                            <button
                                type="button"
                                className="z-20 basis-1/5 peer"
                                aria-label="Adicionar ao carrinho"
                                disabled={isAdding}
                                onClick={handleAddToCart}
                            >
                                <Image
                                    src="/assets/icons/more_cart.svg"
                                    height={30}
                                    width={36}
                                    alt=""
                                    className="cursor-pointer"
                                />
                            </button>
                        ) : (
                            'Produto indisponível'
                        )}
                        {hasStock ? (
                            <div className="absolute w-[100px] peer-hover:w-full z-10 border  text-center rounded-full h-full text-lg bg-white top-0 left-0 text-white border-tertiary transition-all duration-200"></div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default React.memo(ProductCard)
